export * from "./routes";
export * from "./env";

// export all schemas as their own named exports
export type Schemas = components["schemas"] & {
  Cart: operations["cart-create"]["responses"]["200"]["content"]["application/json"]["cart"];
};

// Export operations for use in other packages
export type { operations };

import type { components, operations, paths } from "./generated/client";
export { Paths } from "./generated/types";

// Define the base generic types with proper type guards
type RequestBody<T extends keyof operations> = operations[T] extends {
  requestBody?: infer R;
}
  ? R extends { content: { "application/json": unknown } }
    ? R["content"]["application/json"]
    : never
  : never;

// Updated ResponseBody to handle various response status codes
type ResponseBody<T extends keyof operations> = operations[T] extends {
  responses: infer R;
}
  ? {
      [StatusCode in keyof R]: R[StatusCode] extends {
        content: { "application/json": unknown };
      }
        ? R[StatusCode]["content"]["application/json"]
        : never;
    }[Extract<keyof R, 200 | 201>]
  : never;

// Extract parameter types from operations
type ParameterTypes<T extends keyof operations> = operations[T] extends {
  parameters: infer P;
}
  ? P
  : never;

// Create a type helper that generates all operation types
type OperationKeys = keyof operations;

/**
 * Get the path for a specific operation
 * This type extracts the path from the paths object for a given operation
 */
type OperationPath<T extends keyof operations> = Extract<
  keyof paths,
  {
    [P in keyof paths]: paths[P] extends Record<string, operations[T]>
      ? P
      : never;
  }[keyof paths]
>;

export type ApiTypes = {
  [K in OperationKeys]: {
    /** The request body type for this operation */
    request: RequestBody<K>;
    /** The response body type for this operation */
    response: ResponseBody<K>;
    /** The parameter types for this operation */
    parameters: ParameterTypes<K>;
    /** The API path for this operation */
    path: OperationPath<K>;
  };
};

export const EventTypes = {
  ADD_TO_CART: "ADD_TO_CART", // used for optimistic updates only
  CHECKOUT_STARTED: "CHECKOUT_STARTED", // used for tracking and optimistic updates
  CLICK: "CLICK", // used for optimistic updates only
  CREATE_CART: "CREATE_CART", // used for optimistic updates only
  LOCALE_CHANGE: "LOCALE_CHANGE", // used for optimistic updates when locale settings change
  PAGE_VISIT: "PAGE_VISIT", // used for tracking and optimistic updates
  REMOVE_FROM_CART: "REMOVE_FROM_CART", // used for optimistic updates only
  SETTINGS_AVAILABLE: "SETTINGS_AVAILABLE", // used to notify components when settings are available
  SHARE_VISIT: "SHARE_VISIT", // used for optimistic updates only
  UPDATE_CART_ITEM: "UPDATE_CART_ITEM", // used for optimistic updates only
} as const;

export type EventType = (typeof EventTypes)[keyof typeof EventTypes];

export const TrackableEventTypes: Partial<typeof EventTypes> = {
  CHECKOUT_STARTED: EventTypes.CHECKOUT_STARTED,
  PAGE_VISIT: EventTypes.PAGE_VISIT,
};

export type AttributionData = Schemas["RequestMetadata"]["attribution"];
